import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

/**
 * External dependencies
 */
import debugFactory from 'debug';
import interpolateComponents from 'interpolate-components';
import Tannin from 'tannin';
import LRU from 'lru';
import sha1 from 'hash.js/lib/hash/sha/1';
import { EventEmitter } from 'events';
import sprintf from '@tannin/sprintf';
/**
 * Internal dependencies
 */

import numberFormat from './number-format';
/**
 * Module variables
 */

var debug = debugFactory('i18n-calypso');
/**
 * Constants
 */

var decimal_point_translation_key = 'number_format_decimals';
var thousands_sep_translation_key = 'number_format_thousands_sep';
var domain_key = 'messages';
var translationLookup = [// By default don't modify the options when looking up translations.
function (options) {
  return options;
}];
var hashCache = {}; // raise a console warning

function warn() {
  if (!I18N.throwErrors) {
    return;
  }

  if ('undefined' !== typeof window && window.console && window.console.warn) {
    window.console.warn.apply(window.console, arguments);
  }
} // turns Function.arguments into an array


function simpleArguments(args) {
  return Array.prototype.slice.call(args);
}
/**
 * Coerce the possible arguments and normalize to a single object.
 *
 * @param   {any} args - arguments passed in from `translate()`
 * @returns {object}         - a single object describing translation needs
 */


function normalizeTranslateArguments(args) {
  var original = args[0]; // warn about older deprecated syntax

  if (typeof original !== 'string' || args.length > 3 || args.length > 2 && typeof args[1] === 'object' && typeof args[2] === 'object') {
    warn('Deprecated Invocation: `translate()` accepts ( string, [string], [object] ). These arguments passed:', simpleArguments(args), '. See https://github.com/Automattic/i18n-calypso#translate-method');
  }

  if (args.length === 2 && typeof original === 'string' && typeof args[1] === 'string') {
    warn('Invalid Invocation: `translate()` requires an options object for plural translations, but passed:', simpleArguments(args));
  } // options could be in position 0, 1, or 2
  // sending options as the first object is deprecated and will raise a warning


  var options = {};

  for (var i = 0; i < args.length; i++) {
    if (typeof args[i] === 'object') {
      options = args[i];
    }
  } // `original` can be passed as first parameter or as part of the options object
  // though passing original as part of the options is a deprecated approach and will be removed


  if (typeof original === 'string') {
    options.original = original;
  } else if (typeof options.original === 'object') {
    options.plural = options.original.plural;
    options.count = options.original.count;
    options.original = options.original.single;
  }

  if (typeof args[1] === 'string') {
    options.plural = args[1];
  }

  if (typeof options.original === 'undefined') {
    throw new Error('Translate called without a `string` value as first argument.');
  }

  return options;
}
/**
 * Takes translate options object and coerces to a Tannin request to retrieve translation.
 *
 * @param   {object} tannin  - tannin data object
 * @param   {object} options - object describing translation
 * @returns {string}         - the returned translation from Tannin
 */


function getTranslationFromTannin(tannin, options) {
  return tannin.dcnpgettext(domain_key, options.context, options.original, options.plural, options.count);
}

function getTranslation(i18n, options) {
  for (var i = translationLookup.length - 1; i >= 0; i--) {
    var lookup = translationLookup[i](Object.assign({}, options)); // Only get the translation from tannin if it exists.

    if (i18n.state.locale[lookup.original]) {
      return getTranslationFromTannin(i18n.state.tannin, lookup);
    }
  }

  return null;
}

function I18N() {
  if (!(this instanceof I18N)) {
    return new I18N();
  }

  this.defaultLocaleSlug = 'en'; // Tannin always needs a plural form definition, or it fails when dealing with plurals.

  this.defaultPluralForms = function (n) {
    return n === 1 ? 0 : 1;
  };

  this.state = {
    numberFormatSettings: {},
    tannin: undefined,
    locale: undefined,
    localeSlug: undefined,
    textDirection: undefined,
    translations: LRU({
      max: 100
    })
  };
  this.componentUpdateHooks = [];
  this.translateHooks = [];
  this.stateObserver = new EventEmitter(); // Because the higher-order component can wrap a ton of React components,
  // we need to bump the number of listeners to infinity and beyond
  // FIXME: still valid?

  this.stateObserver.setMaxListeners(0); // default configuration

  this.configure();
}

I18N.throwErrors = false;

I18N.prototype.on = function () {
  var _this$stateObserver;

  (_this$stateObserver = this.stateObserver).on.apply(_this$stateObserver, arguments);
};

I18N.prototype.off = function () {
  var _this$stateObserver2;

  (_this$stateObserver2 = this.stateObserver).off.apply(_this$stateObserver2, arguments);
};

I18N.prototype.emit = function () {
  var _this$stateObserver3;

  (_this$stateObserver3 = this.stateObserver).emit.apply(_this$stateObserver3, arguments);
};
/**
 * Formats numbers using locale settings and/or passed options.
 *
 * @param   {string|number}  number to format (required)
 * @param   {number|object}  options  Number of decimal places or options object (optional)
 * @returns {string}         Formatted number as string
 */


I18N.prototype.numberFormat = function (number) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var decimals = typeof options === 'number' ? options : options.decimals || 0;
  var decPoint = options.decPoint || this.state.numberFormatSettings.decimal_point || '.';
  var thousandsSep = options.thousandsSep || this.state.numberFormatSettings.thousands_sep || ',';
  return numberFormat(number, decimals, decPoint, thousandsSep);
};

I18N.prototype.configure = function (options) {
  Object.assign(this, options || {});
  this.setLocale();
};

I18N.prototype.setLocale = function (localeData) {
  var _this$state$locale$te, _this$state$locale$, _this$state$locale$$m;

  if (localeData && localeData[''] && localeData['']['key-hash']) {
    var keyHash = localeData['']['key-hash'];

    var transform = function transform(string, hashLength) {
      var lookupPrefix = hashLength === false ? '' : String(hashLength);

      if (typeof hashCache[lookupPrefix + string] !== 'undefined') {
        return hashCache[lookupPrefix + string];
      }

      var hash = sha1().update(string).digest('hex');

      if (hashLength) {
        return hashCache[lookupPrefix + string] = hash.substr(0, hashLength);
      }

      return hashCache[lookupPrefix + string] = hash;
    };

    var generateLookup = function generateLookup(hashLength) {
      return function (options) {
        if (options.context) {
          options.original = transform(options.context + String.fromCharCode(4) + options.original, hashLength);
          delete options.context;
        } else {
          options.original = transform(options.original, hashLength);
        }

        return options;
      };
    };

    if (keyHash.substr(0, 4) === 'sha1') {
      if (keyHash.length === 4) {
        translationLookup.push(generateLookup(false));
      } else {
        var variableHashLengthPos = keyHash.substr(5).indexOf('-');

        if (variableHashLengthPos < 0) {
          var hashLength = Number(keyHash.substr(5));
          translationLookup.push(generateLookup(hashLength));
        } else {
          var minHashLength = Number(keyHash.substr(5, variableHashLengthPos));
          var maxHashLength = Number(keyHash.substr(6 + variableHashLengthPos));

          for (var _hashLength = minHashLength; _hashLength <= maxHashLength; _hashLength++) {
            translationLookup.push(generateLookup(_hashLength));
          }
        }
      }
    }
  } // if localeData is not given, assumes default locale and reset


  if (!localeData || !localeData[''].localeSlug) {
    this.state.locale = {
      '': {
        localeSlug: this.defaultLocaleSlug,
        plural_forms: this.defaultPluralForms
      }
    };
  } else if (localeData[''].localeSlug === this.state.localeSlug) {
    // Exit if same data as current (comparing references only)
    if (localeData === this.state.locale) {
      return;
    } // merge new data into existing one


    Object.assign(this.state.locale, localeData);
  } else {
    this.state.locale = Object.assign({}, localeData);
  }

  this.state.localeSlug = this.state.locale[''].localeSlug; // extract the `textDirection` info (LTR or RTL) from either:
  // - the translation for the special string "ltr" (standard in Core, not present in Calypso)
  // - or the `momentjs_locale.textDirection` property present in Calypso translation files

  this.state.textDirection = ((_this$state$locale$te = this.state.locale["text direction\x04ltr"]) === null || _this$state$locale$te === void 0 ? void 0 : _this$state$locale$te[0]) || ((_this$state$locale$ = this.state.locale['']) === null || _this$state$locale$ === void 0 ? void 0 : (_this$state$locale$$m = _this$state$locale$.momentjs_locale) === null || _this$state$locale$$m === void 0 ? void 0 : _this$state$locale$$m.textDirection);
  this.state.tannin = new Tannin(_defineProperty({}, domain_key, this.state.locale)); // Updates numberFormat preferences with settings from translations

  this.state.numberFormatSettings.decimal_point = getTranslationFromTannin(this.state.tannin, normalizeTranslateArguments([decimal_point_translation_key]));
  this.state.numberFormatSettings.thousands_sep = getTranslationFromTannin(this.state.tannin, normalizeTranslateArguments([thousands_sep_translation_key])); // If translation isn't set, define defaults.

  if (this.state.numberFormatSettings.decimal_point === decimal_point_translation_key) {
    this.state.numberFormatSettings.decimal_point = '.';
  }

  if (this.state.numberFormatSettings.thousands_sep === thousands_sep_translation_key) {
    this.state.numberFormatSettings.thousands_sep = ',';
  }

  this.stateObserver.emit('change');
};

I18N.prototype.getLocale = function () {
  return this.state.locale;
};
/**
 * Get the current locale slug.
 *
 * @returns {string} The string representing the currently loaded locale
 **/


I18N.prototype.getLocaleSlug = function () {
  return this.state.localeSlug;
};
/**
 * Get the current text direction, left-to-right (LTR) or right-to-left (RTL).
 *
 * @returns {boolean} `true` in case the current locale has RTL text direction
 */


I18N.prototype.isRtl = function () {
  return this.state.textDirection === 'rtl';
};
/**
 * Adds new translations to the locale data, overwriting any existing translations with a matching key.
 *
 * @param {object} localeData Locale data
 */


I18N.prototype.addTranslations = function (localeData) {
  for (var prop in localeData) {
    if (prop !== '') {
      this.state.tannin.data.messages[prop] = localeData[prop];
    }
  }

  this.stateObserver.emit('change');
};
/**
 * Checks whether the given original has a translation.
 *
 * @returns {boolean} whether a translation exists
 */


I18N.prototype.hasTranslation = function () {
  return !!getTranslation(this, normalizeTranslateArguments(arguments));
};
/**
 * Exposes single translation method.
 * See sibling README
 *
 * @returns {string|object} translated text or an object containing React children that can be inserted into a parent component
 */


I18N.prototype.translate = function () {
  var options = normalizeTranslateArguments(arguments);
  var translation = getTranslation(this, options);

  if (!translation) {
    // This purposefully calls tannin for a case where there is no translation,
    // so that tannin gives us the expected object with English text.
    translation = getTranslationFromTannin(this.state.tannin, options);
  } // handle any string substitution


  if (options.args) {
    var sprintfArgs = Array.isArray(options.args) ? options.args.slice(0) : [options.args];
    sprintfArgs.unshift(translation);

    try {
      translation = sprintf.apply(void 0, _toConsumableArray(sprintfArgs));
    } catch (error) {
      if (!window || !window.console) {
        return;
      }

      var errorMethod = this.throwErrors ? 'error' : 'warn';

      if (typeof error !== 'string') {
        window.console[errorMethod](error);
      } else {
        window.console[errorMethod]('i18n sprintf error:', sprintfArgs);
      }
    }
  } // interpolate any components


  if (options.components) {
    translation = interpolateComponents({
      mixedString: translation,
      components: options.components,
      throwErrors: this.throwErrors
    });
  } // run any necessary hooks


  this.translateHooks.forEach(function (hook) {
    translation = hook(translation, options);
  });
  return translation;
};
/**
 * Causes i18n to re-render all translations.
 *
 * This can be necessary if an extension makes changes that i18n is unaware of
 * and needs those changes manifested immediately (e.g. adding an important
 * translation hook, or modifying the behaviour of an existing hook).
 *
 * If at all possible, react components should try to use the more local
 * updateTranslation() function inherited from the mixin.
 */


I18N.prototype.reRenderTranslations = function () {
  debug('Re-rendering all translations due to external request');
  this.stateObserver.emit('change');
};

I18N.prototype.registerComponentUpdateHook = function (callback) {
  this.componentUpdateHooks.push(callback);
};

I18N.prototype.registerTranslateHook = function (callback) {
  this.translateHooks.push(callback);
};

export default I18N;